// ENG-3154: redesigned Sent record status page, gated by `sent_status_redesign`.
// Frontend only -- it renders the same payload as the legacy sentRecordStatus.jsx.
import React from 'react';
import PropTypes from 'prop-types';
import { ArrowClockwise, XCircle } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import moment from 'moment-timezone';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  RedoOutlined,
} from '@ant-design/icons';

import Button from '../Button';

import { verifyFeatureAccess } from '../../global/featureFlags';
import { formatValidFaxNumber, validFaxNumber } from '../../global/validation';
import { captureAnalytics } from '../../actions/preload';
import {
  RESEND_BLOCKED_HINT, canResendFromStatus, openStatusPageResend, retryingLabel,
} from './failedFaxVisibility';
import './sentRecordStatusRedesign.scss';

const ACCESS_LABELS = {
  can_view_and_send: 'View and send',
  can_view: 'View only',
  revoked: 'Revoked',
};

// fax_status is the delivery state for every channel: fax webhooks write it for faxes,
// the email workers and Mailgun webhook (mail_controller#get_icon) write it for email.
const DELIVERY_STATUS = {
  success: { label: 'Delivered', tone: 'success', Icon: CheckCircleOutlined },
  inprogress: { label: 'Delivery pending', tone: 'pending', Icon: ClockCircleOutlined },
  partialsuccess: { label: 'Partially delivered', tone: 'failure', Icon: CloseCircleOutlined },
  failure: { label: 'Failed', tone: 'failure', Icon: CloseCircleOutlined },
  scheduled: { label: 'Scheduled', tone: 'pending', Icon: ClockCircleOutlined },
  queued: { label: 'Queued', tone: 'pending', Icon: ClockCircleOutlined },
  // A fax that did not go through and was sent again as a new fax (ENG-3679, from the
  // server's `resent`), or a referral reply written as `resent` before it.
  resent: { label: 'Resent', tone: 'neutral', Icon: RedoOutlined },
};

const formatTime = (value, timezone) => moment.tz(value, timezone).format('lll');

const orNA = (value) => value || 'N/A';

// Fax numbers read as 214-774-0007, like the Sent table. Emails pass through first:
// validFaxNumber strips non-digits, so 2125550199@clinic.test would pass as a fax.
const formatAddress = (value) => {
  if (!value || value.includes('@')) return value;
  return validFaxNumber(value).valid ? formatValidFaxNumber(value) : value;
};

// Patient sends (?patient=true) carry no delivery state in this payload, so they get
// no pill rather than an unverified "Sent". An unmapped status is shown neutral, never
// as a success it may not be.
const deliveryStatus = (data) => {
  if (!data.fax_status) return null;
  // ENG-3679: fax_status stays the failure; `resent` says a newer fax took over.
  if (data.resent === true) return DELIVERY_STATUS.resent;
  const retrying = retryingLabel(data);
  if (retrying) return { ...DELIVERY_STATUS.inprogress, label: retrying };
  return DELIVERY_STATUS[data.fax_status] || {
    label: data.fax_status[0].toUpperCase() + data.fax_status.substr(1),
    tone: 'neutral',
    Icon: InfoCircleOutlined,
  };
};

// The count only moves when someone opens the Medsender copy (secure link or access
// code). A fax read off the recipient's machine never reaches us, so say so.
export const viewCountHint = (sentAsFax) => (sentAsFax
  ? 'Digital views only. This counts when the recipient opens the record online with the access code. Reading the paper fax is not counted.'
  : 'Digital views only. This counts each time the recipient opens the secure link.');

// direct_fax transmits the document itself (cover sheet optional); any other fax
// transmits only an access-code page, so the document is read online.
const accessSummary = (data, isAtaFax, sentAsFax, isFax) => {
  if (isAtaFax) return 'Received at their fax machine. There is no digital copy to share.';
  if (data.record_status === 'revoked') return 'Access revoked. The recipient can no longer open this record.';
  if (sentAsFax) return 'The full document was faxed to the recipient.';
  if (isFax) return 'An access code was faxed. The recipient opens the record online with it.';
  return 'The recipient can open this record online.';
};

// ENG-3679: the status page of the other attempt of a resend.
export const resendAttemptPath = (attempt) => (
  `/app/records/status/record/${attempt.file_token}/receiver/${attempt.send_token}`
);

// "Resent …" on a fax that was sent again, "This is a resend …" on the fax that resent
// it. Each links to the other attempt: in the Sent drawer `onOpenAttempt` moves the
// drawer to it, on the full status page it is a link.
const ResendBanners = ({ data, timezone, onOpenAttempt }) => {
  const track = (direction) => captureAnalytics('sent_resend_attempt_opened', {
    direction,
    placement: onOpenAttempt ? 'status_drawer' : 'status_page',
  });
  const attemptLink = (attempt, direction, label) => {
    if (!attempt || !attempt.send_token || !attempt.file_token) return null;
    if (onOpenAttempt) {
      return (
        <button
          type="button"
          className="sent-status-redesign__banner-link"
          onClick={() => { track(direction); onOpenAttempt(attempt); }}
        >
          {label}
        </button>
      );
    }
    return (
      <Link className="sent-status-redesign__banner-link" to={resendAttemptPath(attempt)} onClick={() => track(direction)}>
        {label}
      </Link>
    );
  };

  return (
    <>
      {data.resent === true && (
        <div className="sent-status-redesign__banner sent-status-redesign__banner--resent" role="status" data-testid="sent-status-resent">
          <RedoOutlined />
          <span>
            {data.resent_as && data.resent_as.date_sent
              ? `Resent ${formatTime(data.resent_as.date_sent, timezone)}. `
              : 'Resent. '}
            This fax did not go through, so it was sent again as a new fax.
          </span>
          {attemptLink(data.resent_as, 'resend', 'View the new fax')}
        </div>
      )}
      {data.resent_from && (
        <div className="sent-status-redesign__banner" role="status" data-testid="sent-status-resent-from">
          <InfoCircleOutlined />
          <span>
            This is a resend of a fax that did not go through
            {data.resent_from.date_sent ? ` on ${formatTime(data.resent_from.date_sent, timezone)}` : ''}
            .
          </span>
          {attemptLink(data.resent_from, 'original', 'View the original')}
        </div>
      )}
    </>
  );
};

const resendAttemptShape = PropTypes.shape({
  send_token: PropTypes.string,
  file_token: PropTypes.string,
  fax_status: PropTypes.string,
  date_sent: PropTypes.string,
});

ResendBanners.propTypes = {
  data: PropTypes.shape({
    resent: PropTypes.bool,
    resent_as: resendAttemptShape,
    resent_from: resendAttemptShape,
  }).isRequired,
  timezone: PropTypes.string.isRequired,
  onOpenAttempt: PropTypes.func,
};

ResendBanners.defaultProps = {
  onOpenAttempt: undefined,
};

const SentRecordStatusRedesign = ({
  data, openModal, fileToken, patient, onResent, onOpenAttempt,
}) => {
  const timezone = moment.tz.guess();
  const isAtaFax = data.is_ata_fax === true;
  // Not fax_status: email sends carry one too. `receiver` is receiver_fax ||
  // receiver_email; a patient send's phone number is an SMS, not a fax.
  const isFax = !patient && !String(data.receiver).includes('@');
  const sentAsFax = data.record_status === 'direct_fax';
  const isRevoked = data.record_status === 'revoked';
  const status = deliveryStatus(data);
  const StatusIcon = status && status.Icon;

  const handleResend = () => openStatusPageResend({
    data, fileToken, openModal, verifyFeatureAccess, captureAnalytics, onResent,
  });

  const handleRevoke = async () => {
    const canRevoke = await verifyFeatureAccess('revoke_record');
    if (canRevoke) {
      return openModal({ type: 'REVOKE_RECORDS', data: { recordId: fileToken, sendToken: data.send_token } });
    }
    return openModal({ type: 'RAISE_FLAG', featureFlag: 'revoke_record' });
  };

  const details = [
    { label: 'Patient', value: data.patient_name || 'Not Provided' },
    { label: 'Date of birth', value: data.patient_dob || 'Not Provided' },
    { label: 'Memo', value: orNA(data.memo) },
    // Separate rows, not "X of Y": a fax's transmitted pages include the cover sheet,
    // so the sent count can exceed the original record's.
    { label: 'Pages sent', value: orNA(data.total_pages_sent) },
    { label: 'Pages in original record', value: orNA(data.num_pages) },
  ];
  if (isFax) {
    details.push({
      label: 'Delivery completed',
      value: data.completed_at ? formatTime(data.completed_at, timezone) : 'Delivery pending',
    });
    details.push({
      label: 'Access expires',
      value: data.expiry ? formatTime(data.expiry, timezone) : 'N/A',
    });
  } else {
    details.push({ label: 'Access', value: ACCESS_LABELS[data.record_status] || 'N/A' });
  }

  // resent_at is oldest first; show newest first, with the original send last.
  const sends = [...(data.resent_at || [])].reverse();
  const receiverName = data.receiver_name;
  const senderName = data.sender_name;

  return (
    <div className="sent-status-redesign" data-testid="sent-status-redesign">
      <section className="sent-status-redesign__card sent-status-redesign__hero">
        <div className="sent-status-redesign__parties">
          <div>
            <p className="sent-status-redesign__eyebrow">Sent to</p>
            <h1 className="sent-status-redesign__recipient">{receiverName || formatAddress(data.receiver)}</h1>
            {receiverName && <p className="sent-status-redesign__address">{formatAddress(data.receiver)}</p>}
          </div>
          {/* Patient sends carry no sender in this payload; a fax with no recorded
              source number carries the sender's name alone. */}
          {(data.sender || senderName) && (
            <div data-testid="sent-status-sender">
              <p className="sent-status-redesign__eyebrow">Sent from</p>
              <p className="sent-status-redesign__sender">{senderName || formatAddress(data.sender)}</p>
              {senderName && data.sender && <p className="sent-status-redesign__address">{formatAddress(data.sender)}</p>}
            </div>
          )}
        </div>
        {status && (
          <span className={`sent-status-redesign__pill sent-status-redesign__pill--${status.tone}`}>
            <StatusIcon />
            {status.label}
          </span>
        )}
      </section>

      {isRevoked && (
        <div className="sent-status-redesign__banner" role="status">
          <ExclamationCircleOutlined />
          The recipient&#39;s access to view this record has been revoked.
        </div>
      )}

      {data.fax_error_message && (
        <div className="sent-status-redesign__banner sent-status-redesign__banner--error" role="alert">
          <CloseCircleOutlined />
          {data.fax_error_message}
        </div>
      )}

      <ResendBanners data={data} timezone={timezone} onOpenAttempt={onOpenAttempt} />

      <div className="sent-status-redesign__tiles">
        <section className="sent-status-redesign__card sent-status-redesign__tile">
          <p className="sent-status-redesign__label">Recipient access</p>
          <p className="sent-status-redesign__tile-body">{accessSummary(data, isAtaFax, sentAsFax, isFax)}</p>
        </section>
        {!isAtaFax && (
          <section className="sent-status-redesign__card sent-status-redesign__tile" data-testid="sent-status-view-count">
            <p className="sent-status-redesign__label">
              <EyeOutlined />
              Digital views
            </p>
            <p className="sent-status-redesign__count">
              {data.view_count ? data.view_count : 'Not viewed yet'}
            </p>
            <p className="sent-status-redesign__hint">{viewCountHint(sentAsFax || isFax)}</p>
          </section>
        )}
      </div>

      <section className="sent-status-redesign__card">
        <h2 className="sent-status-redesign__section-title">Details</h2>
        <dl className="sent-status-redesign__details">
          {details.map(({ label, value }) => (
            <div key={label} className="sent-status-redesign__row">
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="sent-status-redesign__card">
        <h2 className="sent-status-redesign__section-title">Send history</h2>
        <ol className="sent-status-redesign__history">
          <li>
            <span>Last sent</span>
            <span>{formatTime(data.date_sent, timezone)}</span>
          </li>
          {sends.map((date, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={`${date}-${index}`}>
              <span>{index === sends.length - 1 ? 'First sent' : 'Sent'}</span>
              <span>{formatTime(date, timezone)}</span>
            </li>
          ))}
        </ol>
      </section>

      {!isAtaFax && (
        <div className="sent-status-redesign__actions">
          {/* The shared redesign Button (ENG-3679): purple primary, and a destructive
              secondary for Revoke, as in the redesigned modals. */}
          {canResendFromStatus(data, patient) && (
            <Button
              type="primary"
              size="small"
              label="Resend fax"
              leftIcon={<ArrowClockwise size={16} weight="bold" />}
              onClick={handleResend}
              disabled={data.resend_blocked === true}
              title={data.resend_blocked === true ? RESEND_BLOCKED_HINT : undefined}
            />
          )}
          <div className="sent-status-redesign__revoke">
            <Button
              type="secondary"
              size="small"
              label="Revoke access"
              leftIcon={<XCircle size={16} weight="bold" />}
              onClick={handleRevoke}
              disabled={isRevoked || patient}
            />
          </div>
        </div>
      )}
    </div>
  );
};

SentRecordStatusRedesign.defaultProps = {
  patient: false,
  onResent: undefined,
  onOpenAttempt: undefined,
};

SentRecordStatusRedesign.propTypes = {
  data: PropTypes.shape({
    fax_status: PropTypes.string,
    fax_error_message: PropTypes.string,
    receiver: PropTypes.string.isRequired,
    receiver_name: PropTypes.string,
    sender: PropTypes.string,
    sender_name: PropTypes.string,
    patient_name: PropTypes.string,
    patient_dob: PropTypes.string,
    date_sent: PropTypes.string.isRequired,
    resent_at: PropTypes.array,
    completed_at: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    expiry: PropTypes.string,
    record_status: PropTypes.string,
    memo: PropTypes.string,
    total_pages_sent: PropTypes.string,
    num_pages: PropTypes.string,
    is_ata_fax: PropTypes.bool,
    view_count: PropTypes.number,
    send_token: PropTypes.string,
    fax_retry_count: PropTypes.number,
    fax_resend_available: PropTypes.bool,
    resend_blocked: PropTypes.bool,
    resent: PropTypes.bool,
    resent_as: resendAttemptShape,
    resent_from: resendAttemptShape,
  }).isRequired,
  openModal: PropTypes.func.isRequired,
  patient: PropTypes.bool,
  fileToken: PropTypes.string.isRequired,
  // Called with the send token after a successful resend; omitted on the full page.
  onResent: PropTypes.func,
  // ENG-3679: called with the other attempt of a resend; omitted on the full page,
  // where the banner links to that attempt's status page instead.
  onOpenAttempt: PropTypes.func,
};

export default SentRecordStatusRedesign;
